import { Link } from "react-router-dom";
import { ArrowRight, Instagram } from "lucide-react";
import SectionHeading from "../components/ui/SectionHeading.jsx";
import CTABanner from "../components/ui/CTABanner.jsx";
import TestimonialCard from "../components/cards/TestimonialCard.jsx";
import { Reveal, MaskLines, ImageReveal } from "../components/motion/Reveal.jsx";
import useSeo from "../hooks/useSeo.js";
import { TESTIMONIALS, ENRICHED, INSTAGRAM } from "../content.js";

const THREADS = ["Events", "Sports", "Fitness", "Families", "Community"];

const GALLERY = [
  { src: "/oneiros/kat-lifestyle.jpg", alt: "Oneiros members enjoying the club lifestyle", span: "md:col-span-7 aspect-[16/10]" },
  { src: "/oneiros/fac-box-cricket.jpg", alt: "Box cricket at Oneiros", span: "md:col-span-5 aspect-[16/10]" },
  { src: "/oneiros/adajan-kids-arena.jpg", alt: "Kids arena at Oneiros Adajan", span: "md:col-span-4 aspect-[4/3]" },
  { src: "/oneiros/kat-pool.jpg", alt: "Indoor semi-Olympic swimming pool at Oneiros Katargam", span: "md:col-span-4 aspect-[4/3]" },
  { src: "/oneiros/fac-cafe.jpg", alt: "Cafe at Oneiros Sports Club", span: "md:col-span-4 aspect-[4/3]" },
];

export default function Community() {
  useSeo({
    title: "Community | More Than a Club — Oneiros Sports Club Surat",
    description:
      "A community that moves together, grows together and inspires a healthier, happier Surat. Member stories, shared moments and the 5 Lakh Kgs city movement at Oneiros Sports Club.",
    image: "/oneiros/kat-lifestyle.jpg",
  });
  return (
    <>
      <section className="mx-auto max-w-[1440px] px-6 pb-14 pt-36 lg:px-12 lg:pt-48">
        <SectionHeading
          eyebrow="Community"
          lines={["More Than a Club.", "A Community."]}
          copy="A community that moves together, grows together and inspires a healthier, happier Surat."
        />
      </section>

      <section className="border-y border-hairline bg-mist py-20 lg:py-24">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <SectionHeading eyebrow="Together" lines={["A More Active.", "Connected Surat."]} />
          <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-5">
            {THREADS.map((t, i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div data-testid={`community-thread-${t.toLowerCase()}`} className="flex flex-col gap-2 border border-hairline bg-white px-6 py-8 text-center transition-colors hover:border-brand/50">
                  <span className="font-display text-2xl text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <SectionHeading eyebrow="Member Stories" lines={["Proven by", "Customer Experiences."]} />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {TESTIMONIALS.map((t) => (
            <TestimonialCard key={t.id} testimonial={t} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-12">
        <SectionHeading eyebrow="Gallery" lines={["Moments That", "Move Us."]} />
        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12">
          {GALLERY.map((g, i) => (
            <ImageReveal key={g.src} src={g.src} alt={g.alt} className={`w-full ${g.span}`} delay={i * 0.05} />
          ))}
        </div>
      </section>

      <section className="grain bg-navy py-24 text-white lg:py-32">
        <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-[2px] w-8 bg-brand" /> The 5 Lakh Kgs Revolution
              </p>
            </Reveal>
            <MaskLines lines={["5L Kgs.", "One City.", "One Goal."]} className="mt-6 font-display text-5xl leading-[1.02] sm:text-6xl" />
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-white/70">{ENRICHED.mission5l}</p>
            </Reveal>
          </div>
          <div className="grid gap-5">
            {["500,000", "1", "5,00,000"].slice(0, 1).map((n) => (
              <Reveal key={n}>
                <p className="font-display text-[22vw] leading-none text-white/10 lg:text-[10rem]">{n}</p>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <Link data-testid="community-5l-cta" to="/contact" className="group inline-flex w-fit items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-brand-dark">
                Be Part of Something Bigger
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="instagram-section" className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-12">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading eyebrow="Instagram" lines={["Follow the", "Movement."]} />
          <Reveal delay={0.15}>
            <a
              data-testid="instagram-follow-btn"
              href={INSTAGRAM.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-full bg-brand px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300"
            >
              <Instagram className="h-4 w-4" /> Follow @{INSTAGRAM.handle}
            </a>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6 md:gap-5">
          {INSTAGRAM.posts.map((post, i) => (
            <Reveal key={post.id} delay={i * 0.05}>
              <a
                data-testid={`instagram-post-${post.id}`}
                href={INSTAGRAM.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block overflow-hidden rounded-lg"
              >
                <img src={post.img} alt={`Oneiros Instagram — ${post.caption}`} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-navy/90 via-transparent to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  <Instagram className="h-4 w-4 text-white" />
                  <p className="mt-1 text-[10px] leading-snug text-white/90">{post.caption}</p>
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABanner
        eyebrow="Community"
        lines={["Be a Part of", "Something Bigger."]}
        primary={{ label: "Enquire Now", to: "/contact" }}
        secondary={{ label: "View All Events", to: "/events" }}
        testId="community-final-cta"
      />
    </>
  );
}
